/**
 * Access hooks, on the role in the workspace that is OPEN.
 *
 * They used to read `user.workspaces[0].role` — the FIRST membership — so an
 * admin of one workspace who is an employee in another saw the second one's
 * sidebar and settings controls as an admin, and every write came back 403
 * (uiv2 decision D2c, 2026-09-25).
 */
import type { UserRole } from '@bakedshift/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { canAccessFeature, canEditSettings, type Feature } from '@/lib/featureAccess';

export function useActiveRole(): UserRole | null {
  const { user } = useAuth();
  const { workspace, currentRole } = useWorkspace();
  if (!workspace || !user) return currentRole ?? null;
  // The platform admin (the app developer) is the top role in any workspace
  // they belong to — what the API enforces (`effectiveRole`).
  if (user.isPlatformAdmin === true) return 'workspace-admin';
  if (currentRole) return currentRole;
  return user.workspaces.find((w) => w.workspaceId === workspace.id)?.role ?? null;
}

export function useCanAccess(feature: Feature): boolean {
  return canAccessFeature(useActiveRole(), feature);
}

export function useCanEditSettings(): boolean {
  return canEditSettings(useActiveRole());
}
